import React, { useState } from 'react';
import { 
  Typography, 
  TextField, 
  Button, 
  Card, 
  CardContent,
  Grid,
  Box,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Snackbar
} from '@mui/material';
import ReactMarkdown from 'react-markdown';

const BIPTemplateGenerator = () => {
  const [targetBehaviors, setTargetBehaviors] = useState('');
  const [functionOfBehavior, setFunctionOfBehavior] = useState('');
  const [clientInfo, setClientInfo] = useState('');
  const [currentInterventions, setCurrentInterventions] = useState('');
  const [environmentalFactors, setEnvironmentalFactors] = useState('');
  const [generatedBIP, setGeneratedBIP] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const generateBIP = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/generateBIPTemplate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          targetBehaviors,
          functionOfBehavior,
          clientInfo: clientInfo || "Not provided",
          currentInterventions: currentInterventions || "Not provided",
          environmentalFactors: environmentalFactors || "Not provided"
        }),
      });
      if (!response.ok) {
        throw new Error('Failed to generate BIP template');
      }
      const data = await response.json();
      setGeneratedBIP(data.bipTemplate);
    } catch (error) {
      console.error('Error generating BIP template:', error);
      setError('Failed to generate BIP template. Please try again.');
    }
    setLoading(false);
  };

  const copyToClipboard = () => {
    if (!generatedBIP) {
      setError('No content to copy');
      return;
    }

    const textArea = document.createElement('textarea');
    textArea.value = generatedBIP;
    textArea.style.position = 'fixed';  // Avoid scrolling to bottom
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        setSnackbarOpen(true);
      } else {
        throw new Error('Copy command was unsuccessful');
      }
    } catch (err) {
      console.error('Failed to copy: ', err);
      setError('Failed to copy to clipboard. Please try again or copy manually.');
    } finally {
      document.body.removeChild(textArea);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, backgroundColor: 'white' }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Target Behavior (Required)"
            value={targetBehaviors}
            onChange={(e) => setTargetBehaviors(e.target.value)}
            variant="outlined"
            required
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth required>
            <InputLabel>Function of Behavior (Required)</InputLabel>
            <Select
              value={functionOfBehavior}
              label="Function of Behavior (Required)"
              onChange={(e) => setFunctionOfBehavior(e.target.value)}
            >
              <MenuItem value="attention">Attention</MenuItem>
              <MenuItem value="escape">Escape</MenuItem>
              <MenuItem value="access">Access to Tangibles</MenuItem>
              <MenuItem value="sensory">Sensory</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Client Information (Optional)"
            value={clientInfo}
            onChange={(e) => setClientInfo(e.target.value)}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Current Interventions (Optional)"
            value={currentInterventions}
            onChange={(e) => setCurrentInterventions(e.target.value)}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Environmental Factors (Optional)"
            value={environmentalFactors}
            onChange={(e) => setEnvironmentalFactors(e.target.value)}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            onClick={generateBIP}
            disabled={loading || !targetBehaviors || !functionOfBehavior}
            fullWidth
          >
            Generate BIP Template
          </Button>
        </Grid>
      </Grid>
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}><CircularProgress /></Box>}
      {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
      {generatedBIP && (
        <Card elevation={2} sx={{ mt: 4, backgroundColor: 'white' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Generated BIP Template:</Typography>
              <Button 
                variant="outlined" 
                onClick={copyToClipboard}
                disabled={!generatedBIP}
              >
                Copy to Clipboard
              </Button>
            </Box>
            <ReactMarkdown>{generatedBIP}</ReactMarkdown>
          </CardContent>
        </Card>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message="Copied to clipboard"
      />
      <Snackbar
        open={!!error}
        autoHideDuration={3000}
        onClose={() => setError(null)}
        message={error}
      />
    </Paper>
  );
};



export default BIPTemplateGenerator;